// The Model Garden (IMPLEMENTATION_PLAN.md §19).
//
// The picker used to be a reference document with a select button attached: it carried the full
// description, every capability and the whole parameter list, which made choosing a model slow and
// — because a deprecation note was rendered in a `.chip`, and chips are `white-space: nowrap` —
// pushed the dialog wider than the viewport. The picker keeps the choosing. This page keeps the
// reading, and nothing here puts a sentence anywhere it cannot wrap: long-form text is a
// paragraph, a `.field-help` or an ErrorNote, never a chip.

import { useMemo, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { ExternalLink } from "lucide-react";
import { formatBytes } from "../api";
import type { Catalog, GenerationMode, Model, Parameter, Provider } from "../api";
import { EmptyState, ErrorNote, Notice, Skeleton } from "../components/ui";

/** No stylesheet change for one page, so the two rhythms this layout needs are set here. */
const SECTION_GAP: CSSProperties = { gap: "var(--space-6)" };
const STACK_GAP: CSSProperties = { gap: "var(--space-4)" };

export function ModelGardenPage({ catalog }: { catalog: Catalog | null }): ReactElement {
  const [providerId, setProviderId] = useState("");

  const providers = useMemo(() => {
    if (!catalog) return [];
    return providerId === ""
      ? catalog.providers
      : catalog.providers.filter((provider) => provider.id === providerId);
  }, [catalog, providerId]);

  return (
    <div className="page">
      <div className="page-inner">
        <div className="page-header">
          <div>
            <h1 className="page-title">Models</h1>
            <p className="lede">What each model can do, and every parameter it takes.</p>
          </div>
        </div>

        {catalog === null && <LoadingGarden />}

        {catalog !== null && catalog.providers.length > 1 && (
          <div className="toolbar">
            <select
              className="select"
              style={{ width: "auto", maxWidth: "100%" }}
              aria-label="Filter by provider"
              value={providerId}
              onChange={(event) => setProviderId(event.target.value)}
            >
              <option value="">All providers</option>
              {catalog.providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {catalog !== null && catalog.providers.length === 0 && (
          <EmptyState
            title="No models are published."
            hint="The server's catalog is empty. Whoever runs this instance can check its provider configuration."
          />
        )}

        {providers.length > 0 && (
          <div className="col" style={SECTION_GAP}>
            {providers.map((provider) => (
              <ProviderSection key={provider.id} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingGarden(): ReactElement {
  return (
    <div className="col" style={STACK_GAP} aria-busy="true">
      <Skeleton height={20} width="30%" />
      <Skeleton height={180} />
      <Skeleton height={180} />
    </div>
  );
}

function ProviderSection({ provider }: { provider: Provider }): ReactElement {
  return (
    <section className="col" style={STACK_GAP}>
      <div className="row wrap">
        <h2 className="section-title">{provider.display_name}</h2>
        <span className="stamp">
          {provider.models.length} {provider.models.length === 1 ? "model" : "models"}
        </span>
        <span className="grow" />
        {provider.docs_url && <DocsLink href={provider.docs_url} label="Provider reference" />}
      </div>

      {/* A provider with no key still has a catalog worth reading; it just cannot be used yet. */}
      {!provider.configured && (
        <Notice>
          {provider.unavailable_reason ??
            `${provider.display_name} has no API key configured on this server, so these models cannot run yet.`}
        </Notice>
      )}

      {provider.models.map((model) => (
        <ModelCard key={model.id} model={model} />
      ))}
    </section>
  );
}

function ModelCard({ model }: { model: Model }): ReactElement {
  const capabilities = capabilityChips(model);

  return (
    <article className="card col" id={model.id}>
      <div className="row wrap">
        <h3 className="section-title">{model.display_name}</h3>
        <span className="stamp mono">{model.id}</span>
      </div>

      {model.description && <p className="prose">{model.description}</p>}

      {/* Never a chip: a deprecation note is a sentence, and a sentence in a nowrap chip is the
          bug this page was built to fix (§19). */}
      {model.deprecation_note && (
        <ErrorNote title="This model is being withdrawn." detail={model.deprecation_note} />
      )}

      <div className="chip-row">
        {capabilities.map((label) => (
          <span className="chip" key={label}>
            {label}
          </span>
        ))}
      </div>

      {model.tags.length > 0 && (
        <div className="chip-row">
          {model.tags.map((tag) => (
            <span className="chip" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {model.capabilities.max_input_images > 0 && (
        <p className="field-help">{inputsSentence(model)}</p>
      )}

      {model.pricing_note && <p className="field-help">{model.pricing_note}</p>}

      <ParameterTable parameters={model.parameters} />

      {model.docs_url && <DocsLink href={model.docs_url} label="Model reference" />}
    </article>
  );
}

/** Reference material, so it starts folded: choosing needs the card, not the whole table. */
function ParameterTable({ parameters }: { parameters: Parameter[] }): ReactElement {
  if (parameters.length === 0) {
    return <p className="field-help">Takes nothing but the prompt.</p>;
  }

  return (
    <details>
      <summary className="stamp" style={{ cursor: "pointer" }}>
        Parameters ({parameters.length})
      </summary>

      {/* Scrolls inside its own box rather than widening the page (§21.1). */}
      <div className="table-scroll" style={{ marginTop: "var(--space-3)" }}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Key</th>
              <th scope="col">Applies to</th>
              <th scope="col">Default</th>
              <th scope="col">Values</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((parameter) => (
              <tr key={parameter.key}>
                <td>
                  <div className="row wrap">
                    <span>{parameter.label}</span>
                    {parameter.experimental && (
                      <span className="badge-experimental">Experimental</span>
                    )}
                  </div>
                  {parameter.description && (
                    <p className="field-help">{parameter.description}</p>
                  )}
                </td>
                <td className="mono">{parameter.key}</td>
                <td>{appliesLabel(parameter.applies_to)}</td>
                <td className="mono">{defaultLabel(parameter)}</td>
                <td>
                  {valuesLabel(parameter) ?? "—"}
                  {parameter.visible_when && (
                    <p className="field-help">
                      Shown when <span className="mono">{parameter.visible_when.key}</span> is{" "}
                      {parameter.visible_when.equals_any.join(" or ")}.
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function DocsLink({ href, label }: { href: string; label: string }): ReactElement {
  return (
    <a className="row" href={href} target="_blank" rel="noreferrer">
      <span>{label}</span>
      <ExternalLink size={13} aria-hidden />
    </a>
  );
}

// ── Labels ─────────────────────────────────────────────────────────────────────────────────

/** Short enough for a chip, every one of them: two or three words, no sentences. */
function capabilityChips(model: Model): string[] {
  const { capabilities } = model;
  const chips: string[] = [];

  if (capabilities.supports_generate) chips.push("Generate");
  if (capabilities.supports_edit) chips.push("Edit");
  if (capabilities.supports_mask) chips.push("Mask");
  if (capabilities.max_input_images === 1) chips.push("1 input");
  if (capabilities.max_input_images > 1) chips.push(`Up to ${capabilities.max_input_images} inputs`);
  if (capabilities.max_outputs > 1) chips.push(`Up to ${capabilities.max_outputs} outputs`);
  if (capabilities.returns_text) chips.push("Returns text");

  return chips;
}

function inputsSentence(model: Model): string {
  const { accepted_input_mime_types, max_input_bytes } = model.capabilities;
  const types = accepted_input_mime_types
    .map((type) => type.replace("image/", "").toUpperCase())
    .join(", ");
  const kinds = types === "" ? "Reference images" : `Reference images as ${types}`;
  return `${kinds}, up to ${formatBytes(max_input_bytes)} each.`;
}

function appliesLabel(modes: GenerationMode[]): string {
  const generate = modes.includes("generate");
  const edit = modes.includes("edit");
  if (generate && edit) return "Both";
  if (edit) return "Edit";
  if (generate) return "Generate";
  return "—";
}

/** A missing default means the key is left out of the provider request entirely (§5.3). */
function defaultLabel(parameter: Parameter): string {
  const declared = parameter.default;
  if (declared === undefined || declared === null) return "—";
  return String(declared);
}

function valuesLabel(parameter: Parameter): string | null {
  if (parameter.kind === "enum") {
    const options = parameter.options ?? [];
    return options.length === 0 ? null : options.map((option) => option.label).join(", ");
  }

  if (parameter.kind === "boolean") return "On or off";

  if (parameter.kind === "text") {
    return parameter.max_length === undefined ? null : `Up to ${parameter.max_length} characters`;
  }

  const parts: string[] = [];
  if (parameter.min !== undefined && parameter.max !== undefined) {
    parts.push(`${parameter.min} to ${parameter.max}`);
  } else if (parameter.min !== undefined) {
    parts.push(`${parameter.min} or more`);
  } else if (parameter.max !== undefined) {
    parts.push(`Up to ${parameter.max}`);
  }
  if (parameter.step !== undefined) parts.push(`in steps of ${parameter.step}`);

  return parts.length === 0 ? null : parts.join(", ");
}
